import { useEffect, useMemo, useState } from 'react';
import {
  UserPlus,
  Search,
  Upload,
  Plus,
  Edit2,
  Trash2,
  ChevronDown,
  ChevronRight,
  X,
  Save,
  FileSpreadsheet,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { supabase } from '../../lib/supabase';
import { fetchAllRows } from '../../lib/fetchAllRows';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

// Status enum from the recruiting spec. The order here drives the modal
// grid layout — keep it grouped (intake / no-answer / decision / pipeline /
// terminal / segmentation) so recruiters can scan it quickly.
const STATUSES = [
  'new',
  'contacted',
  'not_answering',
  'not_answering_2nd_time',
  'not_answering_3rd_time',
  'interested',
  'not_interested',
  'approved',
  'drug_test_scheduled',
  'drug_test_pass',
  'failed_drug_test',
  'failed_mvr',
  'hired',
  'archived',
  'haitian',
  'vm',
  'other',
] as const;
type LeadStatus = (typeof STATUSES)[number];

const STATUS_LABEL: Record<LeadStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  not_answering: 'Not Answering',
  not_answering_2nd_time: 'Not Answering 2nd',
  not_answering_3rd_time: 'Not Answering 3rd',
  interested: 'Interested',
  not_interested: 'Not Interested',
  approved: 'Approved',
  drug_test_scheduled: 'Drug Test Scheduled',
  drug_test_pass: 'Drug Test Pass',
  failed_drug_test: 'Failed Drug Test',
  failed_mvr: 'Failed MVR',
  hired: 'Hired',
  archived: 'Archived',
  haitian: 'Haitian',
  vm: 'VM',
  other: 'Other',
};

const STATUS_COLOR: Record<LeadStatus, string> = {
  new: 'bg-slate-100 text-slate-700 border-slate-300',
  contacted: 'bg-blue-100 text-blue-800 border-blue-300',
  not_answering: 'bg-amber-100 text-amber-800 border-amber-300',
  not_answering_2nd_time: 'bg-amber-200 text-amber-900 border-amber-400',
  not_answering_3rd_time: 'bg-orange-200 text-orange-900 border-orange-400',
  interested: 'bg-green-100 text-green-800 border-green-300',
  not_interested: 'bg-rose-100 text-rose-800 border-rose-300',
  approved: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  drug_test_scheduled: 'bg-cyan-100 text-cyan-800 border-cyan-300',
  drug_test_pass: 'bg-teal-100 text-teal-800 border-teal-300',
  failed_drug_test: 'bg-red-200 text-red-900 border-red-400',
  failed_mvr: 'bg-red-300 text-red-900 border-red-500',
  hired: 'bg-emerald-200 text-emerald-900 border-emerald-500',
  archived: 'bg-slate-300 text-slate-800 border-slate-400',
  haitian: 'bg-purple-100 text-purple-800 border-purple-300',
  vm: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300',
  other: 'bg-zinc-100 text-zinc-700 border-zinc-300',
};

const INTERESTED_OPTIONS = ['Company Driver', 'Owner Operator', 'Lease', 'Rent'];

interface Lead {
  id: string;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  email: string | null;
  interested_in: string | null;
  status: string;
  source: string | null;
  notes: string | null;
  recruiter: string | null;
  contact_attempts: number | null;
  last_contacted: string | null;
  created_at: string;
  updated_at: string;
}

// Fuzzy column-name → DB-field mapping for CSV/Excel import.
const COLUMN_HINTS: Record<string, string[]> = {
  first_name: ['first name', 'firstname', 'first', 'fname', 'given name'],
  last_name: ['last name', 'lastname', 'last', 'lname', 'surname', 'family name'],
  name: ['name', 'full name', 'fullname', 'driver name'],
  phone: ['phone', 'phone number', 'mobile', 'cell', 'telephone', 'tel'],
  email: ['email', 'e-mail', 'email address'],
  interested_in: ['interested in', 'interest', 'type', 'driver type', 'position'],
  status: ['status', 'state'],
  source: ['source', 'lead source', 'origin'],
  notes: ['notes', 'note', 'comments', 'comment', 'remarks'],
  recruiter: ['recruiter', 'rep', 'agent', 'assigned to'],
};

const DB_FIELDS = Object.keys(COLUMN_HINTS) as Array<keyof typeof COLUMN_HINTS>;

export function LeadsView() {
  const { user } = useAuth();
  const { showToast } = useToast();

  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [recruiters, setRecruiters] = useState<string[]>([]);
  const [currentUserFullName, setCurrentUserFullName] = useState<string>('');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterInterested, setFilterInterested] = useState<string>('');
  const [filterRecruiter, setFilterRecruiter] = useState<string>('');

  // Day-group collapse state
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set());

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [statusLead, setStatusLead] = useState<Lead | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);

  useEffect(() => {
    fetchLeads();
    fetchRecruiters();
    fetchCurrentUserName();
  }, []);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const data = await fetchAllRows<any>(() => supabase
        .from('crm_leads')
        .select('*')
        .order('created_at', { ascending: false }));
      setLeads((data as Lead[]) || []);
    } catch (error) {
      console.error('Error fetching leads:', error);
      showToast('error', 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecruiters = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('full_name, role')
        .in('role', ['Recruiting', 'recruiter', 'recruiting + maintenance']);
      if (error) throw error;
      const names = (data || [])
        .map((p: any) => p.full_name)
        .filter((n: string | null): n is string => !!n);
      // De-duplicate while keeping order.
      setRecruiters(Array.from(new Set(names)));
    } catch (error) {
      console.error('Error fetching recruiters:', error);
    }
  };

  const fetchCurrentUserName = async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from('profiles')
      .select('full_name')
      .eq('id', user.id)
      .single();
    if (data?.full_name) setCurrentUserFullName(data.full_name);
  };

  // ------------------------------------------------------------------
  // Filtering + day grouping
  // ------------------------------------------------------------------
  const filteredLeads = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return leads.filter(l => {
      if (filterStatus && l.status !== filterStatus) return false;
      if (filterInterested && l.interested_in !== filterInterested) return false;
      if (filterRecruiter && l.recruiter !== filterRecruiter) return false;
      if (q) {
        const haystack = `${l.name || ''} ${l.first_name || ''} ${l.last_name || ''} ${l.email || ''} ${l.phone || ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [leads, searchTerm, filterStatus, filterInterested, filterRecruiter]);

  const groupedByDay = useMemo(() => {
    const groups = new Map<string, Lead[]>();
    for (const lead of filteredLeads) {
      const day = lead.created_at.slice(0, 10); // YYYY-MM-DD
      const arr = groups.get(day) ?? [];
      arr.push(lead);
      groups.set(day, arr);
    }
    return Array.from(groups.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [filteredLeads]);

  const toggleDay = (day: string) => {
    setCollapsedDays(prev => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  };

  // ------------------------------------------------------------------
  // CRUD
  // ------------------------------------------------------------------
  const handleDelete = async (id: string) => {
    if (!confirm('Delete this lead?')) return;
    try {
      const { error } = await supabase.from('crm_leads').delete().eq('id', id);
      if (error) throw error;
      showToast('success', 'Lead deleted');
      fetchLeads();
    } catch (error) {
      console.error('Delete failed:', error);
      showToast('error', 'Failed to delete lead');
    }
  };

  const handleQuickStatusChange = async (lead: Lead, newStatus: LeadStatus) => {
    try {
      const { error } = await supabase
        .from('crm_leads')
        .update({ status: newStatus })
        .eq('id', lead.id);
      if (error) throw error;
      showToast('success', `Status → ${STATUS_LABEL[newStatus]}`);
      setStatusLead(null);
      fetchLeads();
    } catch (error) {
      console.error('Status update failed:', error);
      showToast('error', 'Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading leads…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-indigo-100 p-3 rounded-lg">
                <UserPlus className="w-8 h-8 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Leads</h1>
                <p className="text-slate-600">Track potential drivers — add, qualify, and update status</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowImportModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <Upload className="w-4 h-4" />
                Import CSV/Excel
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Lead
              </button>
            </div>
          </div>

          {/* Filters row */}
          <div className="mt-6 flex flex-wrap gap-3">
            <div className="flex-1 min-w-[260px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, or phone…"
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">All Statuses</option>
              {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
            <select
              value={filterInterested}
              onChange={e => setFilterInterested(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">All Interests</option>
              {INTERESTED_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            <select
              value={filterRecruiter}
              onChange={e => setFilterRecruiter(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">All Recruiters</option>
              {recruiters.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div className="mt-4 text-xs text-slate-500">
            Showing <strong className="text-slate-700">{filteredLeads.length}</strong> of{' '}
            <strong className="text-slate-700">{leads.length}</strong> leads
          </div>
        </div>

        {/* Day-grouped table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                <tr>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Name</th>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Contact</th>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Interested In</th>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Status</th>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Recruiter</th>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide w-[28rem]">Notes</th>
                  <th className="px-3 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Added</th>
                  <th className="px-3 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupedByDay.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-16 text-center text-slate-500">
                      No leads found. Add one with the button above.
                    </td>
                  </tr>
                )}
                {groupedByDay.map(([day, dayLeads]) => {
                  const isCollapsed = collapsedDays.has(day);
                  const dayLabel = new Date(day + 'T00:00:00').toLocaleDateString(undefined, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  });
                  return (
                    <>
                      <tr key={`day-${day}`} className="bg-slate-100 border-y border-slate-200">
                        <td colSpan={8} className="px-3 py-2">
                          <button
                            onClick={() => toggleDay(day)}
                            className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900"
                          >
                            {isCollapsed
                              ? <ChevronRight className="w-4 h-4" />
                              : <ChevronDown className="w-4 h-4" />}
                            <span>{dayLabel}</span>
                            <span className="text-xs font-normal text-slate-500">
                              · {dayLeads.length} lead{dayLeads.length === 1 ? '' : 's'}
                            </span>
                          </button>
                        </td>
                      </tr>
                      {!isCollapsed && dayLeads.map(lead => {
                        const fullName = lead.name || `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || '—';
                        const status = (STATUSES as readonly string[]).includes(lead.status) ? (lead.status as LeadStatus) : 'new';
                        return (
                          <tr key={lead.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-3 py-3 align-top">
                              <div className="text-sm font-medium text-slate-900">{fullName}</div>
                            </td>
                            <td className="px-3 py-3 align-top">
                              {lead.email && <div className="text-xs text-indigo-600">{lead.email}</div>}
                              {lead.phone && <div className="text-xs text-slate-600 mt-0.5">{lead.phone}</div>}
                              {!lead.email && !lead.phone && <div className="text-xs text-slate-400">—</div>}
                            </td>
                            <td className="px-3 py-3 align-top">
                              <span className="text-xs text-slate-700">{lead.interested_in || '—'}</span>
                            </td>
                            <td className="px-3 py-3 align-top">
                              <button
                                onClick={() => setStatusLead(lead)}
                                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer hover:opacity-80 transition-opacity ${STATUS_COLOR[status]}`}
                              >
                                {STATUS_LABEL[status]}
                              </button>
                            </td>
                            <td className="px-3 py-3 align-top">
                              <span className="text-xs text-slate-700">{lead.recruiter || '—'}</span>
                            </td>
                            <td className="px-3 py-3 align-top">
                              {lead.notes ? (
                                <div className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed" title={lead.notes}>
                                  {lead.notes}
                                </div>
                              ) : <span className="text-xs text-slate-400">—</span>}
                            </td>
                            <td className="px-3 py-3 align-top">
                              <span className="text-xs text-slate-500 whitespace-nowrap">
                                {new Date(lead.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                              </span>
                            </td>
                            <td className="px-3 py-3 align-top">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => setEditingLead(lead)}
                                  className="p-1.5 text-slate-600 hover:bg-slate-100 rounded"
                                  title="Edit"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(lead.id)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {(showAddModal || editingLead) && (
        <LeadFormModal
          lead={editingLead}
          recruiters={recruiters}
          defaultRecruiter={currentUserFullName}
          onClose={() => { setShowAddModal(false); setEditingLead(null); }}
          onSaved={() => { setShowAddModal(false); setEditingLead(null); fetchLeads(); }}
        />
      )}

      {statusLead && (
        <StatusModal
          lead={statusLead}
          onClose={() => setStatusLead(null)}
          onPick={(s) => handleQuickStatusChange(statusLead, s)}
        />
      )}

      {showImportModal && (
        <ImportModal
          onClose={() => setShowImportModal(false)}
          onImported={() => { setShowImportModal(false); fetchLeads(); }}
        />
      )}
    </div>
  );
}

// ===================================================================
// Add / Edit modal
// ===================================================================
function LeadFormModal(props: {
  lead: Lead | null;
  recruiters: string[];
  defaultRecruiter: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { lead, recruiters, defaultRecruiter, onClose, onSaved } = props;
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: lead?.first_name || '',
    last_name: lead?.last_name || '',
    email: lead?.email || '',
    phone: lead?.phone || '',
    interested_in: lead?.interested_in || '',
    status: (lead?.status as LeadStatus) || 'new',
    recruiter: lead?.recruiter || defaultRecruiter || '',
    notes: lead?.notes || '',
    source: lead?.source || '',
  });

  const save = async () => {
    if (!form.first_name.trim()) {
      showToast('error', 'First name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        name: `${form.first_name} ${form.last_name}`.trim(),
        status: form.status,
      };
      if (lead) {
        const { error } = await supabase.from('crm_leads').update(payload).eq('id', lead.id);
        if (error) throw error;
        showToast('success', 'Lead updated');
      } else {
        const { error } = await supabase.from('crm_leads').insert([payload]);
        if (error) throw error;
        showToast('success', 'Lead added');
      }
      onSaved();
    } catch (err: any) {
      console.error('Save lead failed:', err);
      showToast('error', err?.message || 'Failed to save lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">{lead ? 'Edit Lead' : 'Add Lead'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">First Name *</label>
              <input
                type="text"
                value={form.first_name}
                onChange={e => setForm({ ...form, first_name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Last Name</label>
              <input
                type="text"
                value={form.last_name}
                onChange={e => setForm({ ...form, last_name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Phone</label>
              <input
                type="text"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Interested In</label>
              <select
                value={form.interested_in}
                onChange={e => setForm({ ...form, interested_in: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">—</option>
                {INTERESTED_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
              <select
                value={form.status}
                onChange={e => setForm({ ...form, status: e.target.value as LeadStatus })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              >
                {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Recruiter</label>
              <select
                value={form.recruiter}
                onChange={e => setForm({ ...form, recruiter: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">—</option>
                {recruiters.map(r => <option key={r} value={r}>{r}</option>)}
                {form.recruiter && !recruiters.includes(form.recruiter) && (
                  <option value={form.recruiter}>{form.recruiter}</option>
                )}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Source</label>
              <input
                type="text"
                value={form.source}
                onChange={e => setForm({ ...form, source: e.target.value })}
                placeholder="e.g. Website, Referral, Indeed"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              rows={5}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===================================================================
// Status grid modal
// ===================================================================
function StatusModal(props: {
  lead: Lead;
  onClose: () => void;
  onPick: (s: LeadStatus) => void;
}) {
  const { lead, onClose, onPick } = props;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Change Status</h2>
            <p className="text-xs text-slate-500 mt-0.5">{lead.name || `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || '—'}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 gap-2">
            {STATUSES.map(s => (
              <button
                key={s}
                onClick={() => onPick(s)}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all hover:scale-[1.02] ${STATUS_COLOR[s]} ${lead.status === s ? 'ring-2 ring-offset-1 ring-indigo-500' : ''}`}
              >
                {STATUS_LABEL[s]}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ===================================================================
// Import CSV/Excel modal — 2 steps (upload → map → insert in chunks).
// Uses fuzzy header matching against COLUMN_HINTS.
// ===================================================================
function ImportModal(props: { onClose: () => void; onImported: () => void }) {
  const { onClose, onImported } = props;
  const { showToast } = useToast();
  const [step, setStep] = useState<'upload' | 'map'>('upload');
  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [importing, setImporting] = useState(false);

  const handleFile = async (file: File) => {
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: '' });
      if (json.length === 0) {
        showToast('error', 'No rows found in file');
        return;
      }
      const rawHeaders = Object.keys(json[0]);
      setRows(json);
      setHeaders(rawHeaders);

      // Auto-guess mapping based on fuzzy match against COLUMN_HINTS.
      const guessed: Record<string, string> = {};
      for (const dbField of DB_FIELDS) {
        const hints = COLUMN_HINTS[dbField];
        const match = rawHeaders.find(h => hints.includes(h.trim().toLowerCase()));
        if (match) guessed[dbField] = match;
      }
      setMapping(guessed);
      setStep('map');
    } catch (err) {
      console.error('Parse failed:', err);
      showToast('error', 'Failed to parse file');
    }
  };

  const runImport = async () => {
    if (!mapping.first_name && !mapping.name) {
      showToast('error', 'Map either "First Name" or "Name" before importing');
      return;
    }
    setImporting(true);
    try {
      const payloads = rows
        .map(r => {
          const obj: Record<string, any> = {};
          for (const dbField of DB_FIELDS) {
            const src = mapping[dbField];
            if (src && r[src] !== undefined && r[src] !== '') {
              obj[dbField] = String(r[src]).trim();
            }
          }
          // Backfill: if first_name is missing but name is, split it.
          if (!obj.first_name && obj.name) {
            const [first, ...rest] = obj.name.split(/\s+/);
            obj.first_name = first;
            if (rest.length) obj.last_name = rest.join(' ');
          }
          // Backfill: if name is missing but first/last is, compose it.
          if (!obj.name && obj.first_name) {
            obj.name = `${obj.first_name} ${obj.last_name || ''}`.trim();
          }
          if (!obj.status) obj.status = 'new';
          return obj;
        })
        .filter(p => p.first_name);

      if (payloads.length === 0) {
        showToast('error', 'No valid rows (each row needs a first name)');
        return;
      }

      // Chunk inserts to keep PostgREST happy on bigger files.
      const chunkSize = 500;
      let inserted = 0;
      for (let i = 0; i < payloads.length; i += chunkSize) {
        const chunk = payloads.slice(i, i + chunkSize);
        const { error } = await supabase.from('crm_leads').insert(chunk);
        if (error) throw error;
        inserted += chunk.length;
      }
      showToast('success', `Imported ${inserted} lead${inserted === 1 ? '' : 's'}`);
      onImported();
    } catch (err: any) {
      console.error('Import failed:', err);
      showToast('error', err?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">
            {step === 'upload' ? 'Import Leads — Upload file' : 'Import Leads — Map columns'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          {step === 'upload' ? (
            <div className="text-center py-8">
              <FileSpreadsheet className="w-12 h-12 text-indigo-500 mx-auto mb-3" />
              <p className="text-sm text-slate-600 mb-4">Select an Excel (.xlsx) or CSV file with one row per lead.</p>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
                className="block mx-auto text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-600 file:text-white hover:file:bg-indigo-700"
              />
            </div>
          ) : (
            <>
              <p className="text-xs text-slate-600 mb-3">
                Found <strong>{rows.length}</strong> rows. Match your file's columns to the lead fields.
                Required: <strong>First Name</strong> (or <strong>Name</strong>).
              </p>
              <div className="space-y-2">
                {DB_FIELDS.map(field => (
                  <div key={field} className="grid grid-cols-2 gap-3 items-center">
                    <label className="text-xs font-medium text-slate-700">{field.replace(/_/g, ' ')}</label>
                    <select
                      value={mapping[field] || ''}
                      onChange={e => setMapping({ ...mapping, [field]: e.target.value })}
                      className="w-full px-3 py-1.5 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">— skip —</option>
                      {headers.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
          {step === 'map' && (
            <button onClick={() => setStep('upload')} className="px-4 py-2 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50">
              Back
            </button>
          )}
          <button onClick={onClose} className="px-4 py-2 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50">
            Cancel
          </button>
          {step === 'map' && (
            <button
              onClick={runImport}
              disabled={importing}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              {importing ? 'Importing…' : `Import ${rows.length} rows`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
